import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { DateInput, FORM_CONTROL_CLASS, FORM_LABEL_CLASS, Modal } from '@safetyhub/ui';
import {
  HEALTH_EXAM_FINDING_COLUMNS,
  HEALTH_EXAM_FINDING_COLUMN_LABELS,
  HEALTH_EXAM_FINDING_ROWS,
  HEALTH_EXAM_FINDING_ROW_LABELS,
  healthExamActionCount,
  type HealthExamFindingColumn,
  type HealthExamFindingRow,
  type HealthExamFindings,
  type LegalObligationRow,
  type RecordHealthExamResultReceiptResponse,
} from '@safetyhub/shared';
import { apiErrorMessage } from '../../lib/api';
import { recordHealthExamResultReceipt } from '../../lib/legalObligationEvidence';
import { todayDateInputInTimezone } from '../../lib/localDate';
import { EditModalFooter } from '../common/EditModalFooter';
import { fulfilledAtToIso } from './EvidenceModal';

export interface HealthExamReceiptModalProps {
  companyId: string;
  siteId: string;
  /** 진단 의무 행(특수 6·12·24개월 · 수시 · 임시 — 진입점 판정은 shared `HEALTH_EXAM_RECEIPT_OBLIGATION_KEYS`) */
  row: LegalObligationRow;
  /** 본문 첫 줄의 굵은 이름 — 기관 서식 이름이 있으면 그것(`agencyDisplayTitle`) */
  title: string;
  /** 현장 유효 시간대 — 날짜 변환과 「오늘」 */
  timezone: string;
  /**
   * 「다음 1회 주기 ½」(제202조② · Q8 ⓑ)을 걸 수 있는 특수건강진단 행 — 호스트가 그 현장 목록에서 고른다(특수 키 · 제외 아님 · 쓰기 막힘 없음 ·
   * `SHORTEN_NEXT` 변형). 서버가 같은 판정을 다시 한다(여기는 편의). 비면 체크 칸을 그리지 않는다
   */
  shortenCandidates: { obligationId: string; title: string }[];
  onClose: () => void;
  onRecorded: (result: RecordHealthExamResultReceiptResponse) => void | Promise<void>;
}

/** 칸 입력값(문자열) — 빈 칸은 0으로 읽는다 */
type Grid = Record<HealthExamFindingRow, Record<HealthExamFindingColumn, string>>;

const emptyGrid = (): Grid =>
  Object.fromEntries(
    HEALTH_EXAM_FINDING_ROWS.map((r) => [r, Object.fromEntries(HEALTH_EXAM_FINDING_COLUMNS.map((c) => [c, ''])) as Record<HealthExamFindingColumn, string>]),
  ) as Grid;

/** 입력 → 계약 형상(0·빈 칸은 뺀다 — 서버도 같은 정규화를 한다). 정수가 아닌 칸이 있으면 null */
function toFindings(grid: Grid): HealthExamFindings | null {
  const out: HealthExamFindings = {};
  for (const r of HEALTH_EXAM_FINDING_ROWS) {
    const row: Partial<Record<HealthExamFindingColumn, number>> = {};
    for (const c of HEALTH_EXAM_FINDING_COLUMNS) {
      const raw = grid[r][c].trim();
      if (raw === '') continue;
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 0) return null;
      if (n > 0) row[c] = n;
    }
    if (Object.keys(row).length > 0) out[r] = row;
  }
  return out;
}

/**
 * 건강진단 **결과표 수령 기록**(M4 ⓐ · 설계 2026-11-03 §3) — 결과표를 송부받은 날과 서식 86 「사후관리 조치 소견 현황」 집계를 옮긴다.
 * 조치 소견(근로금지·작업 전환·근로시간 단축·직업병 확진 의뢰)이 하나라도 있으면 서버가 「사후관리 조치결과 보고」(수령일부터 30일 · 시행규칙
 * 제210조④) 사건을 함께 등록한다. ⛔ 근로자 이름·건강정보 원문은 받지 않는다 — 인원 수만(명단은 결과표 사본이 보고 사건의 증빙으로 붙는다).
 * 진입점은 특수·수시·임시 행(`SiteObligationsPage`의 `EvidenceAction`)뿐이다(일반·배치전은 보고 대상이 아니다 · 서버도 400).
 */
export default function HealthExamReceiptModal({
  companyId,
  siteId,
  row,
  title,
  timezone,
  shortenCandidates,
  onClose,
  onRecorded,
}: HealthExamReceiptModalProps) {
  const today = todayDateInputInTimezone(timezone);
  const [examinedOn, setExaminedOn] = useState(today);
  const [receivedOn, setReceivedOn] = useState(today);
  const [grid, setGrid] = useState<Grid>(emptyGrid);
  const [note, setNote] = useState('');
  const [shortenIds, setShortenIds] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const findings = toFindings(grid);
  // 직업병 유소견자가 있으면 제202조② 2호의 단축 대상일 수 있다 — 안내만(자동 단축은 기각 · 의사의 「단축 불필요」 소견 예외를 집계가 싣지 못한다)
  const occupationalFinding = (findings?.FINDING_OCCUPATIONAL?.TOTAL ?? 0) > 0;
  // 진단 의무 자신(특수 수령)을 맨 앞에
  const candidates = [...shortenCandidates].sort((a, b) => Number(b.obligationId === row.obligationId) - Number(a.obligationId === row.obligationId));
  const actionCount = findings ? healthExamActionCount(findings) : 0;
  const totalOverflow =
    findings !== null &&
    HEALTH_EXAM_FINDING_ROWS.some((r) =>
      HEALTH_EXAM_FINDING_COLUMNS.some((c) => c !== 'TOTAL' && (findings[r]?.[c] ?? 0) > (findings[r]?.TOTAL ?? 0)),
    );
  const mutation = useMutation({
    mutationFn: () =>
      recordHealthExamResultReceipt(companyId, siteId, row.obligationId, {
        examinedAt: fulfilledAtToIso(examinedOn, timezone),
        receivedAt: fulfilledAtToIso(receivedOn, timezone),
        findings: findings ?? {},
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(shortenIds.length > 0 ? { shortenObligationIds: shortenIds } : {}),
      }),
    onSuccess: onRecorded,
    onError: (err) => setErrorMessage(apiErrorMessage(err) ?? '결과표 수령을 기록하지 못했습니다.'),
  });
  const blockedReason = !examinedOn
    ? '진단일을 선택하세요.'
    : !receivedOn
      ? '결과표 수령일을 선택하세요.'
      : receivedOn < examinedOn
        ? '결과표 수령일이 진단일보다 앞설 수 없습니다.'
        : findings === null
          ? '인원은 0 이상의 정수로 적으세요.'
          : totalOverflow
            ? '조치 인원이 그 행의 합계보다 많습니다.'
            : '';
  const isDirty =
    examinedOn !== today ||
    receivedOn !== today ||
    note !== '' ||
    shortenIds.length > 0 ||
    HEALTH_EXAM_FINDING_ROWS.some((r) => HEALTH_EXAM_FINDING_COLUMNS.some((c) => grid[r][c] !== ''));
  const setCell = (r: HealthExamFindingRow, c: HealthExamFindingColumn, value: string) =>
    setGrid((g) => ({ ...g, [r]: { ...g[r], [c]: value } }));

  return (
    <Modal
      open
      onClose={onClose}
      title="결과표 수령 기록"
      size="lg"
      closeLocked={mutation.isPending}
      isDirty={isDirty}
      footer={
        <EditModalFooter
          isDirty={isDirty}
          blockedReason={blockedReason}
          submitLabel="수령 기록"
          submitDisabled={!!blockedReason || mutation.isPending}
          busy={mutation.isPending}
          busyLabel="기록 중..."
          onSubmit={() => {
            if (blockedReason || mutation.isPending) return;
            setErrorMessage(null);
            mutation.mutate();
          }}
          onFallbackClose={onClose}
        />
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-700">
          <span className="font-bold text-ink-900">{title}</span> 결과표를 받은 날과 「사후관리 조치 소견 현황」(별지 제86호서식) 인원을 옮겨
          적으세요. 근로자 이름·건강정보는 적지 않습니다.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="health-exam-examined-on" className={FORM_LABEL_CLASS}>
              진단일
            </label>
            <DateInput
              id="health-exam-examined-on"
              label="진단일"
              value={examinedOn}
              onChange={setExaminedOn}
              max={today}
              disabled={mutation.isPending}
              className="w-full"
            />
          </div>
          <div>
            <label htmlFor="health-exam-received-on" className={FORM_LABEL_CLASS}>
              결과표 수령일
            </label>
            <DateInput
              id="health-exam-received-on"
              label="결과표 수령일"
              value={receivedOn}
              onChange={setReceivedOn}
              max={today}
              disabled={mutation.isPending}
              className="w-full"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <caption className="sr-only">사후관리 조치 소견 현황(인원)</caption>
            <thead>
              <tr className="text-ink-600">
                <th scope="col" className="px-2 py-1 text-left font-semibold">
                  구분
                </th>
                {HEALTH_EXAM_FINDING_COLUMNS.map((c) => (
                  <th key={c} scope="col" className="px-1 py-1 text-center font-semibold">
                    {HEALTH_EXAM_FINDING_COLUMN_LABELS[c]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {HEALTH_EXAM_FINDING_ROWS.map((r) => (
                <tr key={r}>
                  <th scope="row" className="whitespace-nowrap px-2 py-1 text-left font-medium text-ink-800">
                    {HEALTH_EXAM_FINDING_ROW_LABELS[r]}
                  </th>
                  {HEALTH_EXAM_FINDING_COLUMNS.map((c) => (
                    <td key={c} className="px-1 py-1">
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        step={1}
                        aria-label={`${HEALTH_EXAM_FINDING_ROW_LABELS[r]} ${HEALTH_EXAM_FINDING_COLUMN_LABELS[c]}`}
                        className={`${FORM_CONTROL_CLASS} text-center`}
                        value={grid[r][c]}
                        onChange={(e) => setCell(r, c, e.target.value)}
                        disabled={mutation.isPending}
                        placeholder="0"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm text-ink-700" data-testid="health-exam-follow-up-hint">
          {actionCount > 0
            ? `조치 소견 ${actionCount}건 — 「사후관리 조치결과 보고」(결과표 수령일부터 30일 이내) 사건이 함께 등록됩니다.`
            : '조치 소견(근로금지 및 제한 · 작업 전환 · 근로시간 단축 · 직업병 확진 의뢰 안내)이 없으면 사후관리 조치결과 보고 대상이 아닙니다.'}
        </p>
        {candidates.length > 0 && (
          <fieldset className="space-y-1.5" data-testid="health-exam-shorten">
            <legend className={FORM_LABEL_CLASS}>다음 1회 특수건강진단 주기 ½ (시행규칙 제202조②)</legend>
            <p className="text-xs text-ink-600">
              {occupationalFinding
                ? '직업병 유소견자가 있습니다 — 그 작업공정·유해인자의 특수건강진단은 다음 1회 주기를 ½로 줄여야 합니다(의사가 단축이 필요 없다는 소견을 낸 경우는 제외). 해당하는 문서를 고르세요.'
                : '결과표에 주기 단축 소견이 있거나 직업병 유소견자가 발견된 공정이면 해당 특수건강진단 문서를 고르세요.'}
            </p>
            {candidates.map((c) => (
              <label key={c.obligationId} className="flex items-start gap-2 text-sm text-ink-800">
                <input
                  type="checkbox"
                  checked={shortenIds.includes(c.obligationId)}
                  onChange={(e) =>
                    setShortenIds((ids) => (e.target.checked ? [...ids, c.obligationId] : ids.filter((id) => id !== c.obligationId)))
                  }
                  disabled={mutation.isPending}
                />
                <span>{c.title}</span>
              </label>
            ))}
          </fieldset>
        )}
        <div>
          <label htmlFor="health-exam-note" className={FORM_LABEL_CLASS}>
            메모 (선택)
          </label>
          <input
            id="health-exam-note"
            className={FORM_CONTROL_CLASS}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            disabled={mutation.isPending}
          />
        </div>
        {errorMessage && (
          <p role="alert" className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
}

/** 수령 기록 뒤 토스트 — 보고 사건이 생겼나 · 이미 등록된 사건에 이었나 · 대상이 아니었나 */
export function describeHealthExamReceiptRecorded(result: RecordHealthExamResultReceiptResponse): string {
  const shortened =
    result.shortenedResults.length > 0 ? ` 특수건강진단 ${result.shortenedResults.length}건의 다음 1회 주기를 ½로 기록했습니다.` : '';
  if (!result.followUpEvent) {
    return result.actionCount > 0
      ? `결과표 수령을 기록했습니다. 이 현장 목록에 「사후관리 조치결과 보고」가 없어 사건은 등록하지 않았습니다.${shortened}`
      : `결과표 수령을 기록했습니다. 조치 소견이 없어 사후관리 조치결과 보고 대상이 아닙니다.${shortened}`;
  }
  return result.followUpEvent.created
    ? `결과표 수령을 기록했습니다. 「사후관리 조치결과 보고」(30일 이내) 사건이 등록됐습니다.${shortened}`
    : `결과표 수령을 기록했습니다. 같은 날 이미 등록된 「사후관리 조치결과 보고」 사건에 이었습니다.${shortened}`;
}
